/**
 * Itens do itinerário do QCO e importação por CSV — PBI 112887.
 * Contrato: 112887-api-contract-importacao-itinerario-qco (§1 importação, §2 itens).
 */

/** Item da sub-aba "Itinerário" (§2.1 listagem e §2.2 consulta). */
export interface IItemItinerario {
  id: string;
  ordem: number;
  cep: string;
  logradouro: string;
  municipio: string;
  estado: string;
  /**
   * O back serializa com `NON_NULL`: bairro, coordenadas e rastro da importação
   * **somem** do JSON quando vazios. Ausência é célula vazia.
   */
  bairro?: string;
  latitude?: number;
  longitude?: number;
  importado?: boolean;
  nomeUsuarioImportacao?: string;
  nomeArquivoImportacao?: string;
  dataImportacao?: string;
}

/**
 * Corpo do POST (§2.3) e do PUT (§2.4) do item. No PUT, coordenada ou bairro
 * `null` **apaga** o valor gravado — o PUT substitui o endereço inteiro.
 */
export type ItemItinerarioRequest = Pick<
  IItemItinerario,
  'logradouro' | 'municipio' | 'estado' | 'cep'
> & {
  bairro: string | null;
  latitude: number | null;
  longitude: number | null;
};

/** Situação da consulta à base de endereços na validação (§1.1). */
export type SituacaoEnderecoImportacao = 'ENCONTRADO' | 'CEP_NAO_ENCONTRADO';

/** As 7 colunas do arquivo, já normalizadas pelo back (§1.1 `dados`). */
export interface IDadosImportacaoItinerario {
  cep: string;
  logradouro: string;
  bairro: string;
  municipio: string;
  estado: string;
  latitude: string | null;
  longitude: string | null;
}

/**
 * Registro da verificação. Genérico em `dados` porque os pontos de parada
 * (PBI 113368) usam o mesmo envelope com as colunas Número e Km a mais.
 */
export interface IRegistroImportacaoItinerario<
  D extends IDadosImportacaoItinerario = IDadosImportacaoItinerario,
> {
  numLinha: number;
  ordem: number;
  situacaoEndereco: SituacaoEnderecoImportacao;
  /** Ausente quando `ENCONTRADO`. */
  aviso?: string;
  /** Sempre `false` na validação; a tela devolve `true` nas linhas marcadas. */
  cadastrarNaBase: boolean;
  dados: D;
}

export interface IErroImportacaoItinerario {
  numLinha: number;
  mensagem: string;
}

/** Resposta do `validar-importar` (§1.1). */
export interface IValidacaoImportacaoItinerario<
  D extends IDadosImportacaoItinerario = IDadosImportacaoItinerario,
> {
  nomeArquivo: string;
  /** Ausente quando há erro. */
  registrosValidos?: IRegistroImportacaoItinerario<D>[];
  /** Ausente quando não há erro. */
  erros?: IErroImportacaoItinerario[];
  /** Aviso não torna o arquivo inválido: o Salvar segue `valido`. */
  valido: boolean;
}

/** Corpo do `importar` (§1.2). */
export interface IImportarItinerarioRequest<
  D extends IDadosImportacaoItinerario = IDadosImportacaoItinerario,
> {
  nomeArquivo: string;
  registrosValidos: Omit<IRegistroImportacaoItinerario<D>, 'aviso'>[];
}

/** Envelope de escrita do bilhetagem-api (`importar`, POST/PUT do item). */
export interface IRespostaEscritaItinerario<T = unknown> {
  status: string;
  message: string;
  data: T;
}

export const SITUACAO_CEP_NAO_ENCONTRADO: SituacaoEnderecoImportacao =
  'CEP_NAO_ENCONTRADO';

/** Casas decimais com que o back grava e devolve latitude e longitude (`NUMERIC(10,7)`). */
export const CASAS_DECIMAIS_COORDENADA = 7;

/**
 * Coordenada como o back a devolve, com as 7 casas (`19` → `19.0000000`): o JSON traz
 * o número com os zeros, mas o navegador os descarta ao ler. Ausente vira vazio.
 */
export function formatarCoordenada(valor: number | null | undefined): string {
  return valor === null || valor === undefined
    ? ''
    : valor.toFixed(CASAS_DECIMAIS_COORDENADA);
}
